// Streaming layer for the georeference-stripped COGs the server produces at
// ingestion. Only the coarsest overview plus the 512px tiles covering the
// current viewport are fetched (HTTP range requests via geotiff.js), so first
// paint needs a few hundred KB instead of the whole image. Decoded tiles are
// cached as ImageBitmaps with LRU eviction.
import { fromUrl } from "geotiff";
import type { GeoTIFFImage } from "geotiff";

interface Level {
  image: GeoTIFFImage;
  width: number;
  height: number;
  tileW: number;
  tileH: number;
  scale: number; // full-res px per pixel of this level (1 for the full image)
}

const CACHE_MAX = 160; // tiles; 512² RGBA ≈ 1 MB each
const INIT_RETRIES = 3; // transient fetch failures: retry with backoff before giving up
const TILE_RETRIES = 3;

export class CogLayer {
  private levels: Level[] = [];
  private base: ImageBitmap | null = null; // coarsest level, loaded whole as placeholder
  private baseScale = 1;
  private cache = new Map<string, ImageBitmap | "pending" | "error">(); // insertion order = LRU
  private initAttempts = 0;
  private tileAttempts = new Map<string, number>();
  ready = false;
  failed = false; // only after all init retries are exhausted

  constructor(private url: string, private onUpdate: () => void) {
    void this.init();
  }

  private async init(): Promise<void> {
    try {
      const tiff = await fromUrl(this.url);
      const count = await tiff.getImageCount();
      const levels: Level[] = [];
      for (let i = 0; i < count; i++) {
        const image = await tiff.getImage(i);
        levels.push({
          image, width: image.getWidth(), height: image.getHeight(),
          tileW: image.getTileWidth(), tileH: image.getTileHeight(), scale: 1,
        });
      }
      levels.sort((a, b) => b.width - a.width);
      for (const l of levels) l.scale = levels[0].width / l.width;
      this.levels = levels;
      const coarsest = levels[levels.length - 1];
      this.base = await this.readBitmap(coarsest.image, 0, 0, coarsest.width, coarsest.height);
      this.baseScale = coarsest.scale;
      this.ready = true;
    } catch (e) {
      // Usually a transient network hiccup (several modalities fetch ranged
      // requests in parallel), not a missing file — retry before alarming.
      this.initAttempts++;
      if (this.initAttempts <= INIT_RETRIES) {
        console.warn(`COG load failed, retry ${this.initAttempts}/${INIT_RETRIES}`, e);
        window.setTimeout(() => void this.init(), 1000 * 3 ** (this.initAttempts - 1));
        return;
      }
      console.error("COG load failed permanently", e);
      this.failed = true;
    }
    this.onUpdate();
  }

  private async readBitmap(image: GeoTIFFImage, x0: number, y0: number, x1: number, y1: number): Promise<ImageBitmap> {
    const w = x1 - x0;
    const h = y1 - y0;
    const rgb = (await image.readRGB({ window: [x0, y0, x1, y1], interleave: true })) as Uint8Array;
    const rgba = new Uint8ClampedArray(w * h * 4);
    for (let i = 0, j = 0; j < rgba.length; i += 3, j += 4) {
      rgba[j] = rgb[i];
      rgba[j + 1] = rgb[i + 1];
      rgba[j + 2] = rgb[i + 2];
      rgba[j + 3] = 255;
    }
    return createImageBitmap(new ImageData(rgba, w, h));
  }

  /** Draw into a ctx whose current transform maps full-res image px to device px. */
  draw(ctx: CanvasRenderingContext2D, canvasW: number, canvasH: number): void {
    if (!this.ready || !this.base || this.levels.length === 0) return;
    const full = this.levels[0];
    const m = ctx.getTransform();
    const inv = m.inverse();
    let rx0 = Infinity, ry0 = Infinity, rx1 = -Infinity, ry1 = -Infinity;
    for (const [cx, cy] of [[0, 0], [canvasW, 0], [0, canvasH], [canvasW, canvasH]]) {
      const p = inv.transformPoint({ x: cx, y: cy });
      rx0 = Math.min(rx0, p.x); ry0 = Math.min(ry0, p.y);
      rx1 = Math.max(rx1, p.x); ry1 = Math.max(ry1, p.y);
    }
    rx0 = Math.max(0, rx0); ry0 = Math.max(0, ry0);
    rx1 = Math.min(full.width, rx1); ry1 = Math.min(full.height, ry1);
    if (rx1 <= rx0 || ry1 <= ry0) return;

    // pick the smallest level that is (roughly) at least as sharp as the screen
    const devicePerPx = Math.sqrt(Math.abs(m.a * m.d - m.b * m.c)); // device px per full-res image px
    let li = 0;
    for (let i = this.levels.length - 1; i >= 0; i--) {
      if (devicePerPx * this.levels[i].scale <= 1.2) { li = i; break; }
    }
    const level = this.levels[li];

    // blurry placeholder under everything, so missing tiles never flash dark
    ctx.drawImage(this.base, 0, 0, this.base.width, this.base.height,
      0, 0, this.base.width * this.baseScale, this.base.height * this.baseScale);

    const tx0 = Math.floor(rx0 / level.scale / level.tileW);
    const ty0 = Math.floor(ry0 / level.scale / level.tileH);
    const tx1 = Math.ceil(rx1 / level.scale / level.tileW);
    const ty1 = Math.ceil(ry1 / level.scale / level.tileH);
    for (let ty = ty0; ty < ty1; ty++) {
      for (let tx = tx0; tx < tx1; tx++) {
        const key = `${li}/${tx}/${ty}`;
        const hit = this.cache.get(key);
        if (hit instanceof ImageBitmap) {
          this.cache.delete(key); // refresh LRU position
          this.cache.set(key, hit);
          ctx.drawImage(hit,
            tx * level.tileW * level.scale, ty * level.tileH * level.scale,
            hit.width * level.scale, hit.height * level.scale);
        } else if (hit === undefined) {
          this.request(li, tx, ty);
        }
      }
    }
  }

  private request(li: number, tx: number, ty: number): void {
    const key = `${li}/${tx}/${ty}`;
    this.cache.set(key, "pending");
    const level = this.levels[li];
    const x0 = tx * level.tileW;
    const y0 = ty * level.tileH;
    const x1 = Math.min(level.width, x0 + level.tileW);
    const y1 = Math.min(level.height, y0 + level.tileH);
    void this.readBitmap(level.image, x0, y0, x1, y1)
      .then((bmp) => {
        this.cache.delete(key);
        this.cache.set(key, bmp);
        this.tileAttempts.delete(key);
        this.trim();
        this.onUpdate();
      })
      .catch((e) => {
        this.cache.set(key, "error");
        const n = (this.tileAttempts.get(key) ?? 0) + 1;
        this.tileAttempts.set(key, n);
        if (n > TILE_RETRIES) {
          console.error("COG tile failed permanently", key, e);
          return;
        }
        console.warn(`COG tile failed, retry ${n}/${TILE_RETRIES}`, key, e);
        // forget the error after a backoff so the next draw() re-requests it
        window.setTimeout(() => {
          if (this.cache.get(key) === "error") {
            this.cache.delete(key);
            this.onUpdate();
          }
        }, 1000 * 3 ** (n - 1));
      });
  }

  private trim(): void {
    let bitmaps = 0;
    for (const v of this.cache.values()) if (v instanceof ImageBitmap) bitmaps++;
    for (const [key, v] of this.cache) {
      if (bitmaps <= CACHE_MAX) break;
      if (v instanceof ImageBitmap) {
        v.close();
        this.cache.delete(key);
        bitmaps--;
      }
    }
  }
}
